import { useState } from 'react';
import {
    Plus, Search, Edit2, Trash2, Database, AlertCircle, RefreshCw, Zap,
    Copy, Check, Globe
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
    useGetCustomApisQuery,
    useDeleteCustomApiMutation,
    CustomApiData,
} from '../../features/integrations/customApiApi';
import {
    useGetWebhooksQuery,
    useDeleteWebhookMutation,
    WebhookData,
} from '../../features/integrations/webhookApi';
import { CustomApiFormModal } from './components/CustomApiFormModal';
import { WebhookFormModal } from './components/WebhookFormModal';

export default function IntegrationsPage() {
    const [activeTab, setActiveTab] = useState<'custom_api' | 'webhook'>('custom_api');
    const [searchQuery, setSearchQuery] = useState('');
    
    // Modal states
    const [isApiModalOpen, setIsApiModalOpen] = useState(false);
    const [selectedApi, setSelectedApi] = useState<CustomApiData | undefined>(undefined);
    const [isWebhookModalOpen, setIsWebhookModalOpen] = useState(false);
    const [selectedWebhook, setSelectedWebhook] = useState<WebhookData | undefined>(undefined);

    // Copy URL feedback state (keyed by webhook_id or _id)
    const [copiedUrlId, setCopiedUrlId] = useState<string | null>(null);

    // Queries & Mutations
    const { data: customApiRes, isLoading: isApisLoading, refetch: refetchApis } = useGetCustomApisQuery();
    const [deleteCustomApi, { isLoading: isDeletingApi }] = useDeleteCustomApiMutation();

    const { data: webhooksRes, isLoading: isWebhooksLoading, refetch: refetchWebhooks } = useGetWebhooksQuery();
    const [deleteWebhook, { isLoading: isDeletingWebhook }] = useDeleteWebhookMutation();

    const customApis = customApiRes?.data || [];
    const webhooks = webhooksRes?.data || [];

    // Filter Custom APIs
    const filteredApis = customApis.filter((api) =>
        api.name_custom.toLowerCase().includes(searchQuery.toLowerCase()) ||
        api.api_url.toLowerCase().includes(searchQuery.toLowerCase()) ||
        api.api_type.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Filter Webhooks
    const filteredWebhooks = webhooks.filter((wh) =>
        wh.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (wh.description || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (wh.webhook_id || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (wh.webhook_url || '').toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleCopyUrl = (id: string, url?: string) => {
        if (!url) return;
        navigator.clipboard.writeText(url);
        setCopiedUrlId(id);
        toast.success('Webhook receiver URL copied');
        setTimeout(() => setCopiedUrlId(null), 2000);
    };


    const handleDeleteWebhook = async (id: string, name: string) => {
        if (window.confirm(`Are you sure you want to delete the "${name}" webhook?`)) {
            try {
                await deleteWebhook(id).unwrap();
                toast.success('Webhook deleted successfully from CMS');
            } catch (err: any) {
                console.error(err);
                toast.error(err?.data?.message || 'Failed to delete webhook');
            }
        }
    };

    const handleDeleteApi = async (id: string, name: string) => {
        if (window.confirm(`Are you sure you want to delete the "${name}" integration?`)) {
            try {
                await deleteCustomApi(id).unwrap();
                toast.success('Integration deleted successfully');
            } catch (err: any) {
                console.error(err);
                toast.error(err?.data?.message || 'Failed to delete integration');
            }
        }
    };

    const getTypeColor = (type: string) => {
        switch (type) {
            case 'weather':
                return 'bg-blue-50 text-blue-700 border-blue-100';
            case 'prayer':
                return 'bg-emerald-50 text-emerald-700 border-emerald-100';
            case 'forex':
                return 'bg-amber-50 text-amber-700 border-amber-100';
            default:
                return 'bg-purple-50 text-purple-700 border-purple-100';
        }
    };

    const handleRefreshAll = () => {
        if (activeTab === 'custom_api') {
            refetchApis();
        } else {
            refetchWebhooks();
        }
        toast.success('Refreshed data feeds');
    };

    return (
        <>
            <CustomApiFormModal
                isOpen={isApiModalOpen}
                onClose={() => {
                    setIsApiModalOpen(false);
                    setSelectedApi(undefined);
                }}
                initialData={selectedApi}
            />

            <WebhookFormModal
                isOpen={isWebhookModalOpen}
                onClose={() => {
                    setIsWebhookModalOpen(false);
                    setSelectedWebhook(undefined);
                }}
                initialData={selectedWebhook}
            />

            <div className="flex-1 overflow-y-auto w-full custom-scrollbar pb-10 px-6 pt-6 font-sans">
                {/* Header Card */}
                <div className="bg-gradient-to-r from-[#E9F7EF] via-white to-[#F1FAF5] border border-[#e4e7ec] rounded-[14px] p-6 relative overflow-hidden mb-8 shadow-sm">
                    <div className="absolute right-[-40px] top-[-40px] w-[180px] h-[180px] rounded-full bg-[#30A84B]/5 blur-2xl"></div>
                    <div className="flex items-center gap-4 relative z-10">
                        <div className={`p-3.5 rounded-[12px] border ${
                            activeTab === 'webhook'
                                ? 'bg-amber-500/10 text-amber-600 border-amber-500/20'
                                : 'bg-[#30A84B]/10 text-[#30A84B] border-[#30A84B]/20'
                        }`}>
                            {activeTab === 'webhook' ? <Zap className="w-6 h-6 fill-amber-500/20" /> : <Database className="w-6 h-6" />}
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-[#101928]">
                                {activeTab === 'webhook' ? 'Real-Time Webhooks' : 'Custom API Integrations'}
                            </h1>
                            <p className="text-sm text-gray-500 mt-1 max-w-xl">
                                {activeTab === 'webhook'
                                    ? 'Receive instant push notifications and JSON events from external services to stream onto digital signage displays in real time.'
                                    : 'Connect external REST APIs (Weather, Prayer, Forex feeds) that Wejha polls on a timer to dynamically map variables onto visual template layers.'}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Subheader & Tabs */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 mb-9">
                    {/* Source Selector Tabs */}
                    <div className="flex items-center gap-2 p-1.5 bg-gray-100 rounded-xl border border-gray-200 shadow-xs">
                        <button
                            type="button"
                            onClick={() => {
                                setActiveTab('custom_api');
                                setSearchQuery('');
                            }}
                            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                                activeTab === 'custom_api'
                                    ? 'bg-white text-[#30A84B] shadow-sm'
                                    : 'text-gray-500 hover:text-gray-800'
                            }`}
                        >
                            <Globe className="w-3.5 h-3.5" />
                            <span>Custom APIs</span>
                            <span className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] font-mono text-gray-600 font-semibold">
                                {customApis.length}
                            </span>
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setActiveTab('webhook');
                                setSearchQuery('');
                            }}
                            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                                activeTab === 'webhook'
                                    ? 'bg-white text-amber-600 shadow-sm'
                                    : 'text-gray-500 hover:text-gray-800'
                            }`}
                        >
                            <Zap className="w-3.5 h-3.5 text-amber-500 fill-amber-500/20" />
                            <span>Webhooks</span>
                            <span className="px-1.5 py-0.5 bg-amber-50 rounded text-[10px] font-mono text-amber-700 font-semibold">
                                {webhooks.length}
                            </span>
                        </button>
                    </div>

                    {/* Toolbar Actions */}
                    <div className="flex items-center gap-4 w-full sm:w-auto justify-between sm:justify-end">
                        {/* Search Bar */}
                        <div className="bg-white h-[44px] relative rounded-[10px] w-full sm:w-[320px] border border-[#e4e7ec] flex items-center px-3.5 hover:border-gray-300 focus-within:border-[#30A84B] focus-within:ring-2 focus-within:ring-[#30A84B]/15 transition-all shadow-sm">
                            <Search className="w-4 h-4 text-gray-400 shrink-0" />
                            <input
                                type="text"
                                placeholder={activeTab === 'webhook' ? 'Search webhooks...' : 'Search APIs...'}
                                className="font-sans font-normal text-xs w-full text-[#101928] bg-transparent outline-none px-2.5 py-1.5"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>

                        {/* Refresh Button */}
                        <button
                            onClick={handleRefreshAll}
                            className="h-[44px] w-[44px] flex items-center justify-center bg-white border border-[#e4e7ec] text-gray-600 rounded-[10px] hover:bg-gray-50 hover:text-gray-900 transition-colors shadow-sm cursor-pointer shrink-0"
                            title="Refresh data"
                        >
                            <RefreshCw className="w-4 h-4" />
                        </button>

                        {/* New Integration Button */}
                        <button
                            onClick={() => {
                                if (activeTab === 'webhook') {
                                    setSelectedWebhook(undefined);
                                    setIsWebhookModalOpen(true);
                                } else {
                                    setSelectedApi(undefined);
                                    setIsApiModalOpen(true);
                                }
                            }}
                            className={`h-[44px] px-4 py-2.5 text-white font-bold text-xs flex items-center gap-2 rounded-[10px] shadow-sm transition-colors cursor-pointer shrink-0 ${
                                activeTab === 'webhook'
                                    ? 'bg-amber-500 hover:bg-amber-600'
                                    : 'bg-[#30a84b] hover:bg-[#288e3e]'
                            }`}
                        >
                            <Plus className="w-4 h-4" />
                            {activeTab === 'webhook' ? 'New Webhook' : 'New Custom API'}
                        </button>
                    </div>
                </div>

                {/* TAB 1: Custom APIs Grid */}
                {activeTab === 'custom_api' && (
                    <>
                        {isApisLoading ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {[1, 2, 3].map((i) => (
                                    <div key={i} className="bg-white h-[200px] rounded-[12px] border border-gray-100 animate-pulse p-6 flex flex-col justify-between">
                                        <div>
                                            <div className="h-6 w-32 bg-gray-200 rounded mb-2"></div>
                                            <div className="h-4 w-48 bg-gray-150 rounded"></div>
                                        </div>
                                        <div className="h-10 w-full bg-gray-100 rounded-md"></div>
                                    </div>
                                ))}
                            </div>
                        ) : filteredApis.length === 0 ? (
                            <div className="w-full flex flex-col items-center justify-center min-h-[300px] bg-white rounded-[12px] border border-dashed border-gray-200 p-8 shadow-sm">
                                <div className="bg-gray-50 p-4 rounded-full text-gray-400 mb-4">
                                    <Database className="w-8 h-8" />
                                </div>
                                <h3 className="text-base font-semibold text-gray-700">No Integrations Found</h3>
                                <p className="text-xs text-gray-500 mt-1 text-center max-w-sm">
                                    {searchQuery ? 'No integrations match your search criteria.' : 'Connect your first external API to get started mapping data to your template designs.'}
                                </p>
                                {!searchQuery && (
                                    <button
                                        onClick={() => {
                                            setSelectedApi(undefined);
                                            setIsApiModalOpen(true);
                                        }}
                                        className="mt-4 text-[#30a84b] font-bold text-xs hover:underline cursor-pointer flex items-center gap-1.5"
                                    >
                                        <Plus className="w-4 h-4" /> Connect API
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {filteredApis.map((api) => (
                                    <div
                                        key={api._id}
                                        className="bg-white border border-[#e4e7ec] rounded-[14px] p-5 hover:shadow-lg hover:border-gray-300 transition-all duration-300 flex flex-col justify-between min-h-[220px] group relative"
                                    >
                                        <div>
                                            <div className="flex items-start justify-between gap-2 mb-2">
                                                <h3 className="font-bold text-sm text-[#101928] line-clamp-1 group-hover:text-[#30A84B] transition-colors" title={api.name_custom}>
                                                    {api.name_custom}
                                                </h3>
                                                <span className={`px-2 py-0.5 text-[9px] font-bold rounded-full border tracking-wide uppercase shrink-0 ${getTypeColor(api.api_type)}`}>
                                                    {api.api_type}
                                                </span>
                                            </div>

                                            <div className="flex items-center gap-2 mb-4">
                                                <span className="px-1.5 py-0.5 font-mono text-[9px] font-bold bg-slate-100 text-slate-700 rounded border border-slate-200">
                                                    {api.method}
                                                </span>
                                                <span className="font-mono text-xs text-gray-500 truncate select-all" title={api.api_url}>
                                                    {api.api_url}
                                                </span>
                                            </div>

                                            <div className="grid grid-cols-2 gap-3 text-[11px] text-gray-500 mb-6 bg-slate-50 p-2.5 rounded-[8px] border border-gray-100">
                                                <div>
                                                    <span className="block text-gray-400 text-[10px]">Sync Frequency:</span>
                                                    <span className="font-medium text-gray-700">Every {api.refresh_interval || 30} mins</span>
                                                </div>
                                                <div>
                                                    <span className="block text-gray-400 text-[10px]">Cached Fields:</span>
                                                    <span className="font-medium text-gray-700">
                                                        {api.customapidata ? `${Object.keys(api.customapidata).length} keys` : 'No payload'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between border-t border-gray-100 pt-3.5 mt-auto">
                                            <span className="text-[10px] text-gray-400">
                                                {api.last_refreshed_at ? (
                                                    <>Synced: {new Date(api.last_refreshed_at).toLocaleTimeString()}</>
                                                ) : (
                                                    <span className="flex items-center gap-1 text-amber-500">
                                                        <AlertCircle className="w-3 h-3" /> Needs test
                                                    </span>
                                                )}
                                            </span>
                                            <div className="flex gap-1.5">
                                                <button
                                                    onClick={() => {
                                                        setSelectedApi(api);
                                                        setIsApiModalOpen(true);
                                                    }}
                                                    className="p-1.5 text-gray-500 hover:text-[#30A84B] hover:bg-gray-50 rounded-md transition-all cursor-pointer"
                                                    title="Edit configuration"
                                                >
                                                    <Edit2 className="w-3.5 h-3.5" />
                                                </button>
                                                <button
                                                    onClick={() => api._id && handleDeleteApi(api._id, api.name_custom)}
                                                    className="p-1.5 text-red-500 hover:bg-red-50 rounded-md transition-all cursor-pointer"
                                                    title="Delete integration"
                                                    disabled={isDeletingApi}
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}

                {/* TAB 2: Webhooks Grid */}
                {activeTab === 'webhook' && (
                    <>
                        {isWebhooksLoading ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {[1, 2, 3].map((i) => (
                                    <div key={i} className="bg-white h-[200px] rounded-[12px] border border-gray-100 animate-pulse p-6 flex flex-col justify-between">
                                        <div>
                                            <div className="h-6 w-32 bg-gray-200 rounded mb-2"></div>
                                            <div className="h-4 w-48 bg-gray-150 rounded"></div>
                                        </div>
                                        <div className="h-10 w-full bg-gray-100 rounded-md"></div>
                                    </div>
                                ))}
                            </div>
                        ) : filteredWebhooks.length === 0 ? (
                            <div className="w-full flex flex-col items-center justify-center min-h-[300px] bg-white rounded-[12px] border border-dashed border-gray-200 p-8 shadow-sm">
                                <div className="bg-amber-50 p-4 rounded-full text-amber-500 mb-4">
                                    <Zap className="w-8 h-8 fill-amber-500/20" />
                                </div>
                                <h3 className="text-base font-semibold text-gray-700">No Webhooks Registered in CMS</h3>
                                <p className="text-xs text-gray-500 mt-1 text-center max-w-sm">
                                    {searchQuery
                                        ? 'No webhooks match your search term.'
                                        : 'Create your first webhook receiver URL to push real-time event updates to digital signage screens.'}
                                </p>
                                {!searchQuery && (
                                    <button
                                        onClick={() => {
                                            setSelectedWebhook(undefined);
                                            setIsWebhookModalOpen(true);
                                        }}
                                        className="mt-4 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-bold text-xs flex items-center gap-1.5 shadow-sm transition-all cursor-pointer"
                                    >
                                        <Plus className="w-4 h-4" /> Create First Webhook
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {filteredWebhooks.map((wh) => {
                                    const targetId = wh._id || wh.webhook_id;
                                    const payloadKeyCount = wh.latest_payload && typeof wh.latest_payload === 'object'
                                        ? Object.keys(wh.latest_payload).length
                                        : 0;

                                    return (
                                        <div
                                            key={targetId}
                                            className="bg-white border border-[#e4e7ec] rounded-[14px] p-5 hover:shadow-lg hover:border-gray-300 transition-all duration-300 flex flex-col justify-between min-h-[240px] group relative"
                                        >
                                            <div>
                                                {/* Header Row */}
                                                <div className="flex items-start justify-between gap-2 mb-2">
                                                    <div>
                                                        <h3 className="font-bold text-sm text-[#101928] line-clamp-1 group-hover:text-amber-600 transition-colors" title={wh.name}>
                                                            {wh.name}
                                                        </h3>
                                                        {wh.description && (
                                                            <p className="text-[11px] text-gray-500 line-clamp-1 mt-0.5">{wh.description}</p>
                                                        )}
                                                    </div>

                                                </div>

                                                {/* Receiver URL box */}
                                                <div className="mb-3 space-y-1">
                                                    <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider block">
                                                        Receiver Endpoint
                                                    </span>
                                                    <div className="flex items-center gap-1 bg-slate-50 border border-slate-200/80 rounded-lg p-1.5">
                                                        <span className="font-mono text-[10px] text-gray-600 truncate select-all flex-1" title={wh.webhook_url}>
                                                            {wh.webhook_url || `.../receiver/${wh.webhook_id}`}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleCopyUrl(targetId, wh.webhook_url)}
                                                            className="p-1 text-gray-500 hover:text-amber-600 hover:bg-white rounded transition-colors cursor-pointer shrink-0"
                                                            title="Copy receiver URL"
                                                        >
                                                            {copiedUrlId === targetId ? (
                                                                <Check className="w-3.5 h-3.5 text-emerald-600" />
                                                            ) : (
                                                                <Copy className="w-3.5 h-3.5" />
                                                            )}
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Meta Info Box */}
                                                <div className="grid grid-cols-2 gap-2 text-[10px] text-gray-500 mb-4 bg-amber-50/30 p-2.5 rounded-[8px] border border-amber-100">
                                                    <div>
                                                        <span className="block text-gray-400 text-[9px]">Auth Method:</span>
                                                        <span className="font-bold text-gray-700 uppercase">
                                                            {wh.auth_type ? wh.auth_type.replace('_', ' ') : 'None'}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <span className="block text-gray-400 text-[9px]">Latest Payload:</span>
                                                        <span className="font-bold text-gray-700">
                                                            {payloadKeyCount > 0 ? `${payloadKeyCount} keys cached` : 'Awaiting data'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Card Footer Actions */}
                                            <div className="flex items-center justify-between border-t border-gray-100 pt-3.5 mt-auto text-[10px] text-gray-400">
                                                <span className="truncate max-w-[170px]" title={wh.last_received_at}>
                                                    {wh.last_received_at ? (
                                                        <>Updated: {new Date(wh.last_received_at).toLocaleTimeString()}</>
                                                    ) : (
                                                        <span className="text-gray-400">No events received yet</span>
                                                    )}
                                                </span>
                                                <div className="flex gap-1.5 shrink-0">
                                                    <button
                                                        onClick={() => {
                                                            setSelectedWebhook(wh);
                                                            setIsWebhookModalOpen(true);
                                                        }}
                                                        className="p-1.5 text-gray-500 hover:text-amber-600 hover:bg-gray-50 rounded-md transition-all cursor-pointer"
                                                        title="Edit webhook"
                                                    >
                                                        <Edit2 className="w-3.5 h-3.5" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteWebhook(targetId, wh.name)}
                                                        className="p-1.5 text-red-500 hover:bg-red-50 rounded-md transition-all cursor-pointer"
                                                        title="Delete webhook from CMS"
                                                        disabled={isDeletingWebhook}
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    );
}
